import React, {useMemo, useState} from 'react';
import {Alert, Button, Card, DatePicker, Select, Space, Table, Tag} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import dayjs, {Dayjs} from 'dayjs';
import {
  ComplaintTaskContextResponse,
  ComplaintTaskFacetsResponse,
  ComplaintTasksResponse,
  ComplaintTasksVariables,
  ComplaintTaskAssigneesResponse,
  GET_COMPLAINT_TASK_CONTEXT,
  GET_COMPLAINT_TASK_FACETS,
  GET_COMPLAINT_TASKS,
  GET_COMPLAINT_TASK_ASSIGNEES,
} from '../../../graphql/complaintTask/queries';
import {
  COMPLAINT_SUB_TYPES,
  ComplaintOverdue,
  ComplaintTaskCount,
  ComplaintTaskFilterInput,
  ComplaintTaskRow,
} from '../../../graphql/complaintTask/types';
import {translateGraphQLError} from '../../../utils/graphqlUtils';
import AssigneeSelect from './components/AssigneeSelect';
import ComplaintTaskDetailPanel from './components/ComplaintTaskDetailPanel';
import TimelinessTags from './components/TimelinessTags';
import {formatDateTime, formatDuration, formatTime, isOverdue, PRIORITY_COLOR, priorityLabel, roleLabel, subTypeLabel} from './complaintTaskUtils';

type RangeKey = 'today' | 'week' | 'month';

/** 快捷日期（含今天，按本地自然日）：今天 / 近一周（7 天）/ 近一月（如 09-15 ~ 10-14）。 */
const presetRange = (key: RangeKey, today: Dayjs = dayjs().startOf('day')): [Dayjs, Dayjs] => {
  if (key === 'week') return [today.subtract(6, 'day'), today];
  if (key === 'month') return [today.subtract(1, 'month').add(1, 'day'), today];
  return [today, today];
};

const PAGE_SIZE = 20;
/** 后端单页上限 100（ComplaintTaskQueryServiceImpl.MAX_PAGE_SIZE） */
const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];
/** 左栏「分类 Top」只列计数最多的几类，其余从下方全部分类下拉里选 */
const SUB_TYPE_TOP = 6;
/** 自选日期范围上限（10-14 确认），后端 basic 同样校验。 */
const MAX_RANGE_MONTHS = 6;

/** 不能选未来日期；已点了一端时，另一端只能在它前后 6 个月内。 */
const disabledRangeDate = (current: Dayjs, {from}: {from?: Dayjs}) =>
  current.isAfter(dayjs(), 'day')
  || (!!from && (current.isAfter(from.add(MAX_RANGE_MONTHS, 'month'), 'day')
    || current.isBefore(from.subtract(MAX_RANGE_MONTHS, 'month'), 'day')));

const countOf = (list: ComplaintTaskCount[] | undefined, key: string | number) =>
  list?.find((c) => c.key === String(key))?.count ?? 0;

/**
 * QC → 客诉管理平台 → 客诉任务管理（原型 image2 + 详情1/2）。
 * 列表即人工调度工作台：日期范围 + 左栏组合筛选 + 承接人行内指派 / 转派 + 行内展开详情。
 * 数据范围由后端按登录人组织角色过滤（系统管理员全部 / 客服、大区负责人所在 HUB / +1 主管下级 / 本人）。
 */
const ComplaintTaskManagePage: React.FC = () => {
  const {t} = useTranslation();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => presetRange('today'));
  const [status, setStatus] = useState<number | undefined>();
  const [overdue, setOverdue] = useState<ComplaintOverdue | undefined>();
  const [hubIds, setHubIds] = useState<number[]>([]);
  const [priority, setPriority] = useState<number | undefined>();
  const [subType, setSubType] = useState<string | undefined>();
  const [assigneeId, setAssigneeId] = useState<number | undefined>();
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  /** 已展开详情的任务（点行展开 / 收起，不要 +/- 列） */
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const toggleExpand = (taskId: number) => {
    // 拖选文字（如复制运单号）时不切换
    if (window.getSelection()?.toString()) return;
    setExpandedKeys((keys) => (keys.includes(taskId) ? keys.filter((k) => k !== taskId) : [...keys, taskId]));
  };

  const {data: ctxData} = useQuery<ComplaintTaskContextResponse>(GET_COMPLAINT_TASK_CONTEXT);
  const ctx = ctxData?.complaintTaskContext;
  // 承接人列表按登录人层级（系统管理员 / 大区负责人·客服 / +1 主管 / 本人）由后端返回
  const {data: peopleData} = useQuery<ComplaintTaskAssigneesResponse>(GET_COMPLAINT_TASK_ASSIGNEES);
  const assignees = peopleData?.complaintTaskAssignees ?? [];

  // 日期范围按提报时间、本地自然日：[开始日 00:00, 结束日次日 00:00)，换算成 UTC 传给后端（10-14 确认）
  const filter: ComplaintTaskFilterInput = useMemo(() => {
    return {
      startFrom: range[0].startOf('day').toISOString(),
      startTo: range[1].add(1, 'day').startOf('day').toISOString(),
      statuses: status ? [status] : undefined,
      overdue,
      hubIds: hubIds.length ? hubIds : undefined,
      priorities: priority ? [priority] : undefined,
      subTypes: subType ? [subType] : undefined,
      assigneeId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, status, overdue, hubIds, priority, subType, assigneeId]);

  const {data, loading, error, refetch} = useQuery<ComplaintTasksResponse, ComplaintTasksVariables>(GET_COMPLAINT_TASKS, {
    variables: {filter, pageNum, pageSize},
    fetchPolicy: 'network-only',
  });
  const {data: facetData, refetch: refetchFacets} = useQuery<ComplaintTaskFacetsResponse>(GET_COMPLAINT_TASK_FACETS, {
    variables: {filter},
    fetchPolicy: 'network-only',
  });
  const page = data?.complaintTasks;
  const facet = facetData?.complaintTaskFacets;
  const serverNow = page?.serverNow;

  const resetPage = <T, >(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPageNum(1);
  };
  const refresh = () => {
    refetch();
    refetchFacets();
  };

  const columns: ColumnsType<ComplaintTaskRow> = [
    {
      title: t('complaintTask.columns.priority'),
      key: 'priority',
      width: 90,
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={PRIORITY_COLOR[r.priority ?? 3]} className="!mr-0">{priorityLabel(r.priority)}</Tag>
          {r.emotionLevel === 'BURST' && <Tag color="red" bordered={false} className="!mr-0">{t('complaintTask.emotion.BURST')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('complaintTask.columns.customer'),
      key: 'customer',
      width: 200,
      render: (_, r) => (
        <div>
          <div>
            {r.customerLevel && <Tag color="purple" className="!mr-1">{r.customerLevel}</Tag>}
            <span className="font-medium">{r.customerName ?? '—'}</span>
          </div>
          <div className="text-xs text-gray-400">{r.chatName}</div>
          {(r.warehouseName || r.ownerHubName) && (
            <div className="text-xs text-gray-400">{t('complaintTask.columns.warehouseShort')} {r.warehouseName ?? r.ownerHubName}</div>
          )}
        </div>
      ),
    },
    {
      title: t('complaintTask.columns.summary'),
      key: 'summary',
      render: (_, r) => (
        <div>
          <div>{r.title}</div>
          {r.trackingNos.length > 0 && (
            <div className="text-xs text-purple-600">
              {r.trackingNos.slice(0, 2).join('、')}
              {r.trackingNos.length > 2 ? ` ${t('complaintTask.columns.moreTracking', {count: r.trackingNos.length})}` : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('complaintTask.columns.subType'),
      key: 'subType',
      width: 90,
      render: (_, r) => subTypeLabel(r.subType, r.subTypeName),
    },
    {
      title: t('complaintTask.columns.reportTime'),
      key: 'reportTime',
      width: 120,
      render: (_, r) => (
        <div>
          <div>{formatDateTime(r.startAt)}</div>
          <div className="text-xs text-gray-400">
            {r.status === 70 && r.finishAt
              ? t('complaintTask.columns.took', {duration: formatDuration(dayjs(r.finishAt).diff(dayjs(r.startAt)))})
              : t('complaintTask.columns.elapsed', {duration: formatDuration(dayjs(serverNow ?? undefined).diff(dayjs(r.startAt)))})}
          </div>
        </div>
      ),
    },
    {
      title: t('complaintTask.columns.timeliness'),
      key: 'timeliness',
      width: 150,
      render: (_, r) => <TimelinessTags row={r} serverNow={serverNow}/>,
    },
    {
      title: t('complaintTask.columns.status'),
      key: 'status',
      width: 90,
      render: (_, r) => (
        <div>
          <Tag color={r.status === 70 ? 'green' : r.status === 20 ? 'gold' : 'purple'} className="!mr-0">
            {t(`complaintTask.status.${r.status}`)}
          </Tag>
          {r.status === 40 && !r.actualStartAt && (
            <div className="text-xs text-orange-500">{t('complaintTask.status.notResponded')}</div>
          )}
        </div>
      ),
    },
    {
      title: t('complaintTask.columns.assignee'),
      key: 'assignee',
      width: 220,
      render: (_, r) => (
        // 指派下拉的点击（含弹出层）不触发行展开
        <div onClick={(e) => e.stopPropagation()}>
          {r.canAssign
            ? <AssigneeSelect row={r} onAssigned={refresh}/>
            : <span>{r.assigneeName ?? '—'}{r.assigneeRole ? `（${roleLabel(r.assigneeRole)}）` : ''}</span>}
          {r.assignMode && (
            <div className="text-xs text-gray-400">{t(`complaintTask.assignMode.${r.assignMode}`)}</div>
          )}
        </div>
      ),
    },
  ];

  const sideItem = (active: boolean, label: React.ReactNode, count: number, onClick: () => void, color?: string) => (
    <div
      className={`flex justify-between px-3 py-1.5 rounded cursor-pointer ${active ? 'bg-purple-50 text-purple-600 font-medium' : ''}`}
      onClick={onClick}
    >
      <span className={color}>{label}</span>
      <span className="text-gray-400">{count}</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 overflow-auto">
      <div className="flex flex-wrap items-center gap-2">
        <DatePicker.RangePicker
          allowClear={false}
          value={range}
          disabledDate={disabledRangeDate}
          onChange={(v) => {
            if (v?.[0] && v?.[1]) resetPage(setRange)([v[0], v[1]]);
          }}
        />
        {(['today', 'week', 'month'] as RangeKey[]).map((k) => {
          const preset = presetRange(k);
          const active = range[0].isSame(preset[0], 'day') && range[1].isSame(preset[1], 'day');
          return (
            <Button key={k} type={active ? 'primary' : 'default'} ghost={active}
                    onClick={() => resetPage(setRange)(preset)}>
              {t(`complaintTask.filter.${k}`)}
            </Button>
          );
        })}
        {assignees.length > 1 && (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            className="w-48"
            placeholder={t('complaintTask.filter.allAssignees')}
            value={assigneeId}
            onChange={resetPage(setAssigneeId)}
            options={assignees.map((p) => ({
              value: p.accountId,
              label: p.self ? t('complaintTask.work.viewAsMe', {name: p.name}) : p.name,
            }))}
          />
        )}
        <Button onClick={refresh}>{t('complaintTask.filter.refresh')}</Button>
        <span className="text-xs text-gray-400">
          {t('complaintTask.filter.generatedAt', {time: formatTime(serverNow), total: page?.total ?? 0})}
        </span>
      </div>

      <Card size="small">
        <Space size={32}>
          <span>{t('complaintTask.summary.total')} <b className="text-xl">{facet?.total ?? 0}</b></span>
          <span>{t('complaintTask.summary.resolved')} <b className="text-xl text-green-600">{countOf(facet?.byStatus, 70)}</b></span>
          <span>{t('complaintTask.summary.responseOverdue')} <b className="text-xl text-orange-500">{facet?.responseOverdue ?? 0}</b></span>
          <span>{t('complaintTask.summary.resolveOverdue')} <b className="text-xl text-red-500">{facet?.resolveOverdue ?? 0}</b></span>
          <span>{t('complaintTask.summary.high')} <b className="text-xl text-red-500">{countOf(facet?.byPriority, 1)}</b></span>
        </Space>
      </Card>

      <div className="flex gap-4">
        <Card size="small" className="w-60 shrink-0">
          <div className="text-xs text-gray-400 mb-1">{t('complaintTask.filter.status')}</div>
          {sideItem(status === undefined, t('complaintTask.filter.allTasks'), facet?.total ?? 0, () => resetPage(setStatus)(undefined))}
          {[20, 40, 70].map((s) => sideItem(status === s, t(`complaintTask.status.${s}`), countOf(facet?.byStatus, s),
            () => resetPage(setStatus)(status === s ? undefined : s)))}

          <div className="text-xs text-gray-400 mt-3 mb-1">{t('complaintTask.filter.timeliness')}</div>
          {sideItem(overdue === 'RESPONSE', t('complaintTask.summary.responseOverdue'), facet?.responseOverdue ?? 0,
            () => resetPage(setOverdue)(overdue === 'RESPONSE' ? undefined : 'RESPONSE'), 'text-orange-500')}
          {sideItem(overdue === 'RESOLVE', t('complaintTask.summary.resolveOverdue'), facet?.resolveOverdue ?? 0,
            () => resetPage(setOverdue)(overdue === 'RESOLVE' ? undefined : 'RESOLVE'), 'text-red-500')}

          <div className="text-xs text-gray-400 mt-3 mb-1">{t('complaintTask.filter.hub')}</div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            className="w-full"
            placeholder={t('complaintTask.filter.allHubs')}
            value={hubIds}
            onChange={resetPage(setHubIds)}
            options={(ctx?.hubOptions ?? []).map((h) => ({
              value: h.id,
              label: `${h.name} (${countOf(facet?.byHub, h.id)})`,
            }))}
          />

          <div className="text-xs text-gray-400 mt-3 mb-1">{t('complaintTask.filter.priority')}</div>
          <Space size={4}>
            {[1, 2, 3].map((p) => (
              <Tag.CheckableTag key={p} checked={priority === p}
                                onChange={() => resetPage(setPriority)(priority === p ? undefined : p)}>
                {priorityLabel(p)} {countOf(facet?.byPriority, p)}
              </Tag.CheckableTag>
            ))}
          </Space>

          <div className="text-xs text-gray-400 mt-3 mb-1">{t('complaintTask.filter.subTypeTop')}</div>
          {(facet?.bySubType ?? []).slice(0, SUB_TYPE_TOP).map((c) => sideItem(subType === c.key, subTypeLabel(c.key), c.count,
            () => resetPage(setSubType)(subType === c.key ? undefined : c.key)))}
          {/* 全部 17 类下拉：与 Top 列表共用同一个分类筛选 */}
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            className="w-full mt-1"
            placeholder={t('complaintTask.filter.allSubTypes', {count: COMPLAINT_SUB_TYPES.length})}
            value={subType}
            onChange={resetPage(setSubType)}
            options={COMPLAINT_SUB_TYPES.map((code) => ({
              value: code,
              label: `${subTypeLabel(code)} (${countOf(facet?.bySubType, code)})`,
            }))}
          />
        </Card>

        <div className="flex-1 min-w-0">
          {error && <Alert type="error" showIcon className="mb-2"
                           message={translateGraphQLError(error, 'complaintTask.errors.loadFailed')}/>}
          <Table
            rowKey="taskId"
            size="middle"
            loading={loading}
            columns={columns}
            dataSource={page?.list ?? []}
            rowClassName={(r) => `cursor-pointer ${isOverdue(r, serverNow) ? 'bg-yellow-50' : ''}`}
            onRow={(r) => ({onClick: () => toggleExpand(r.taskId)})}
            expandable={{
              expandedRowRender: (r) => <ComplaintTaskDetailPanel taskId={r.taskId}/>,
              showExpandColumn: false,
              expandedRowKeys: expandedKeys,
            }}
            pagination={{
              current: pageNum,
              pageSize,
              total: page?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              showQuickJumper: true,
              showTotal: (total) => t('table.pagination.totalItems', {total}),
              // 改每页条数回到第 1 页，避免当前页超出新的总页数
              onChange: (p, size) => {
                if (size !== pageSize) {
                  setPageSize(size);
                  setPageNum(1);
                } else {
                  setPageNum(p);
                }
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ComplaintTaskManagePage;
